import React, { useState } from "react";
import { Button, Form, Row, Col, Card, Badge, Alert } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";

// Phase 4B, §19/§26. Read-only resolution preview — never writes anything,
// and its output is never trusted as authorization by anything else (the
// real gate re-resolves independently at transition/submission time).
export default function TaskApprovalPolicyPreview({ taskTypes, projects, projectModules, onProjectChange }) {
  const [context, setContext] = useState({ task_type_id: "", project_id: "", project_module_id: "", priority: "", requested_transition_id: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const res = await api.post("/task-approval-policies/preview", {
        task_type_id: context.task_type_id || undefined,
        project_id: context.project_id || undefined,
        project_module_id: context.project_module_id || undefined,
        priority: context.priority || undefined,
        requested_transition_id: context.requested_transition_id || undefined,
      });
      setResult(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to preview policy resolution");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <strong className="small d-block mb-2">Resolution Preview</strong>
        <Row className="g-2">
          <Col xs={12} sm={6} md={3}>
            <Form.Label className="small mb-0">Task Type</Form.Label>
            <Form.Select size="sm" value={context.task_type_id} onChange={(e) => setContext({ ...context, task_type_id: e.target.value })}>
              <option value="">Any</option>
              {(taskTypes || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Form.Select>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Label className="small mb-0">Project</Form.Label>
            <Form.Select size="sm" value={context.project_id} onChange={(e) => { setContext({ ...context, project_id: e.target.value, project_module_id: "" }); onProjectChange?.(e.target.value); }}>
              <option value="">Any</option>
              {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Form.Select>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Label className="small mb-0">Module</Form.Label>
            <Form.Select
              size="sm"
              value={context.project_module_id}
              onChange={(e) => setContext({ ...context, project_module_id: e.target.value })}
              disabled={!context.project_id}
              title={!context.project_id ? "Select a project first to enable this field" : undefined}
            >
              <option value="">Any</option>
              {(projectModules || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Form.Select>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Label className="small mb-0">Priority</Form.Label>
            <Form.Select size="sm" value={context.priority} onChange={(e) => setContext({ ...context, priority: e.target.value })}>
              <option value="">Any</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </Form.Select>
          </Col>
        </Row>
        <div className="tap-preview-footer">
          <Button size="sm" variant="outline-primary" disabled={loading} onClick={runPreview}>
            {loading ? "Checking..." : "Preview Resolution"}
          </Button>
        </div>

        {result && (
          <div className="mt-3">
            {result.approval_required ? (
              <Alert variant="success" className="small mb-2">
                Approval required — matched <strong>{result.policy?.name}</strong> ({result.policy?.code}) at level <Badge bg="light" text="dark">{result.resolution_source}</Badge>
              </Alert>
            ) : (
              <Alert variant="secondary" className="small mb-2">No active policy matches this context — approval is not required.</Alert>
            )}
            {result.stages?.length > 0 && (
              <div className="small mb-2">
                <strong>Expected stages:</strong> {result.stages.map((s) => s.stage_name).join(" → ")}
              </div>
            )}
            {result.skipped_reasons?.length > 0 && (
              <div className="small text-muted">
                <strong>Levels checked:</strong>
                <ul className="mb-0">
                  {result.skipped_reasons.map((s, i) => <li key={i}>{s.level}: {s.reason}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
